import { useEffect, useState, useRef } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'

// Color palette matching the app
const clothingColors = [
    '#E6E5E2', '#D4D3CF', '#C4C3BF', '#CBBFAE', 
    '#B8AC9A', '#A39E8F', '#8B8680', '#6E6B63',
]

// Clothing silhouette types
type ClothingType = 'top' | 'bottom' | 'outerwear' | 'shoes'

function ClothingSilhouette({ type, color, size = 32 }: { type: ClothingType; color: string; size?: number }) {
    const silhouettes: Record<ClothingType, React.ReactElement> = {
        top: (
            <svg viewBox="0 0 56 56" fill={color} style={{ width: size, height: size }}>
                <path d="M14 12 L28 8 L42 12 L48 24 L42 26 L40 18 L40 48 L16 48 L16 18 L14 26 L8 24 Z" />
            </svg>
        ),
        bottom: (
            <svg viewBox="0 0 56 56" fill={color} style={{ width: size, height: size }}>
                <path d="M16 8 L40 8 L40 16 L42 52 L30 52 L28 24 L26 52 L14 52 L16 16 Z" />
            </svg>
        ),
        outerwear: (
            <svg viewBox="0 0 56 56" fill={color} style={{ width: size, height: size }}>
                <path d="M12 10 L28 6 L44 10 L50 26 L44 28 L42 18 L44 52 L12 52 L14 18 L12 28 L6 26 Z" />
            </svg>
        ),
        shoes: (
            <svg viewBox="0 0 40 40" fill={color} style={{ width: size * 0.8, height: size * 0.8 }}>
                <path d="M6 20 L10 14 L30 14 L36 20 L36 28 L6 28 Z" />
            </svg>
        ),
    }
    return <div className="opacity-90">{silhouettes[type]}</div>
}

// Chaotic thoughts that users have
const chaoticThoughts = [
    "I have nothing to wear...",
    "Is this too casual?",
    "What goes with this?",
    "Running late again...",
]

// Mini outfit card for the output
function MiniOutfitCard({ delay, index }: { delay: number; index: number }) {
    const types: ClothingType[] = ['top', 'bottom', 'outerwear', 'shoes']
    const items = types.map((type, i) => ({
        type,
        color: clothingColors[(index * 4 + i) % clothingColors.length],
    }))

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.85, x: 15 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ 
                delay, 
                duration: 0.6, 
                type: 'spring',
                stiffness: 80,
                damping: 15
            }}
            className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-paper-tertiary shadow-lg p-3 sm:p-4"
        >
            {/* Shimmer effect */}
            <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: '200%' }}
                transition={{ delay: delay + 0.5, duration: 1.2, repeat: Infinity, repeatDelay: 4 }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 pointer-events-none rounded-2xl"
            />
            
            <div className="mb-2">
                <span className="text-[9px] uppercase tracking-wider font-bold text-cta">Option {index + 1}</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
                {items.map((item, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: delay + 0.1 + i * 0.05 }}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-paper-alt border border-paper-tertiary/50 flex items-center justify-center"
                    >
                        <ClothingSilhouette type={item.type} color={item.color} size={20} />
                    </motion.div>
                ))}
            </div>
        </motion.div>
    )
}

// Custom brain/neural network SVG visualization
function ProcessingBrain({ isActive }: { isActive: boolean }) {
    return (
        <div className="relative w-20 h-20 sm:w-28 sm:h-28">
            {/* Outer glow - more visible */}
            <motion.div
                animate={isActive ? {
                    scale: [1, 1.4, 1],
                    opacity: [0.4, 0.7, 0.4],
                } : { scale: 1, opacity: 0.3 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -inset-4 bg-accent-subtle rounded-full blur-2xl"
            />
            
            {/* Main brain container - dark filled */}
            <motion.div 
                animate={isActive ? { scale: [1, 1.05, 1] } : { scale: 1 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                className="relative w-full h-full rounded-full overflow-hidden shadow-xl border-2 border-ink/10"
            >
                {/* Dark filled background */}
                <div className="absolute inset-0 bg-gradient-to-br from-ink to-ink-secondary" />
                
                {/* Neural network pattern - light colored nodes on dark bg */}
                <svg 
                    viewBox="0 0 100 100" 
                    className="absolute inset-0 w-full h-full"
                >
                    {/* Connection lines first (behind nodes) */}
                    <motion.line 
                        x1="50" y1="50" x2="30" y2="35" 
                        stroke="#CBBFAE" strokeWidth="2"
                        initial={{ pathLength: 0 }}
                        animate={isActive ? { pathLength: [0, 1, 0], opacity: [0.3, 0.8, 0.3] } : { pathLength: 1, opacity: 0.4 }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <motion.line 
                        x1="50" y1="50" x2="70" y2="35" 
                        stroke="#CBBFAE" strokeWidth="2"
                        initial={{ pathLength: 0 }}
                        animate={isActive ? { pathLength: [0, 1, 0], opacity: [0.3, 0.8, 0.3] } : { pathLength: 1, opacity: 0.4 }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
                    />
                    <motion.line 
                        x1="50" y1="50" x2="30" y2="65" 
                        stroke="#CBBFAE" strokeWidth="2"
                        initial={{ pathLength: 0 }}
                        animate={isActive ? { pathLength: [0, 1, 0], opacity: [0.3, 0.8, 0.3] } : { pathLength: 1, opacity: 0.4 }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
                    />
                    <motion.line 
                        x1="50" y1="50" x2="70" y2="65" 
                        stroke="#CBBFAE" strokeWidth="2"
                        initial={{ pathLength: 0 }}
                        animate={isActive ? { pathLength: [0, 1, 0], opacity: [0.3, 0.8, 0.3] } : { pathLength: 1, opacity: 0.4 }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
                    />
                    
                    {/* Nodes - accent colored */}
                    <circle cx="50" cy="50" r="10" fill="#CBBFAE" />
                    <circle cx="30" cy="35" r="6" fill="#CBBFAE" opacity="0.8" />
                    <circle cx="70" cy="35" r="6" fill="#CBBFAE" opacity="0.8" />
                    <circle cx="30" cy="65" r="6" fill="#CBBFAE" opacity="0.8" />
                    <circle cx="70" cy="65" r="6" fill="#CBBFAE" opacity="0.8" />
                </svg>
                
                {/* Pulsing ring when active */}
                {isActive && (
                    <motion.div
                        animate={{ 
                            scale: [1, 2, 1],
                            opacity: [0.6, 0, 0.6]
                        }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut' }}
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 border-2 border-accent-subtle rounded-full"
                    />
                )}
            </motion.div>
        </div>
    )
}

// Animated flow line
function FlowLine({ isActive }: { isActive: boolean }) {
    return (
        <div className="relative flex-1 h-1 min-w-[24px] sm:min-w-[40px] max-w-[80px]">
            {/* Track background */}
            <div className="absolute inset-0 bg-ink/10 rounded-full" />
            
            {/* Filled portion */}
            <motion.div
                className="absolute inset-y-0 left-0 rounded-full bg-ink/30"
                initial={{ width: 0 }}
                animate={{ width: isActive ? '100%' : 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
            />
        </div>
    )
}

// Vertical flow line for mobile
function FlowLineVertical({ isActive }: { isActive: boolean }) {
    return (
        <div className="relative w-1 h-6 sm:h-8">
            {/* Track background */}
            <div className="absolute inset-0 bg-ink/10 rounded-full" />
            
            {/* Filled portion */}
            <motion.div
                className="absolute inset-x-0 top-0 rounded-full bg-ink/30"
                initial={{ height: 0 }}
                animate={{ height: isActive ? '100%' : 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
            />
        </div>
    )
}

// The chaotic input message - shows typing first, then text
function ChaoticInput({ isTyping, thought }: { isTyping: boolean; thought: string }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="relative"
        >
            {/* Message bubble - fixed height to prevent layout shift */}
            <div className="relative px-4 py-3 sm:px-5 sm:py-4 bg-white/90 backdrop-blur-sm rounded-2xl border border-paper-tertiary shadow-lg w-[160px] sm:w-[200px] min-h-[52px] sm:min-h-[60px] flex items-center">
                <AnimatePresence mode="wait">
                    {isTyping ? (
                        /* Typing indicator */
                        <motion.div
                            key="typing"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="flex gap-1.5 items-center"
                        >
                            {[0, 1, 2].map((i) => (
                                <motion.div
                                    key={i}
                                    animate={{ y: [0, -4, 0] }}
                                    transition={{ 
                                        duration: 0.5, 
                                        repeat: Infinity, 
                                        delay: i * 0.1,
                                        ease: 'easeInOut'
                                    }}
                                    className="w-2 h-2 bg-ink-tertiary rounded-full"
                                />
                            ))}
                        </motion.div>
                    ) : (
                        /* Actual message text */
                        <motion.p
                            key={thought}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="text-sm sm:text-base text-ink font-medium leading-snug"
                        >
                            {thought}
                        </motion.p>
                    )}
                </AnimatePresence>
            </div>
            
            {/* Stress emoji */}
            <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-2 -right-2 text-lg"
            >
                😰
            </motion.div>
        </motion.div>
    )
}

// Main animation component
function TransformationAnimation() {
    const ref = useRef(null)
    const isInView = useInView(ref, { once: false, amount: 0.3 })
    const [thoughtIndex, setThoughtIndex] = useState(0)
    const [phase, setPhase] = useState<'typing' | 'showing' | 'processing' | 'output'>('typing')

    // Phase progression with typing -> showing text -> processing -> output
    useEffect(() => {
        if (!isInView) {
            setPhase('typing')
            return
        }

        const sequence = async () => {
            setPhase('typing')
            await new Promise(r => setTimeout(r, 1400)) // Show typing dots
            setPhase('showing')
            await new Promise(r => setTimeout(r, 1800)) // Show the text
            setPhase('processing')
            await new Promise(r => setTimeout(r, 2200)) // Process through brain
            setPhase('output')
            await new Promise(r => setTimeout(r, 3500)) // Show output
            // Then cycle to next thought
            setThoughtIndex((prev) => (prev + 1) % chaoticThoughts.length)
        }

        sequence()

        // Repeat the full animation cycle
        const interval = setInterval(() => {
            sequence()
        }, 8900)

        return () => clearInterval(interval)
    }, [isInView, thoughtIndex])

    const isTyping = phase === 'typing'

    return (
        <div ref={ref} className="w-full">
            {/* Desktop: Horizontal layout - fixed height */}
            <div className="hidden sm:flex items-center justify-center gap-2 lg:gap-4 min-h-[140px]">
                {/* Input */}
                <ChaoticInput 
                    isTyping={isTyping} 
                    thought={chaoticThoughts[thoughtIndex]}
                />
                
                {/* Flow line 1 */}
                <FlowLine isActive={phase === 'processing' || phase === 'output'} />
                
                {/* Brain */}
                <ProcessingBrain isActive={phase === 'processing'} />
                
                {/* Flow line 2 */}
                <FlowLine isActive={phase === 'output'} />
                
                {/* Output - always reserve space */}
                <div className="flex gap-3">
                    {[0, 1, 2].map((i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.9, y: 10 }}
                            animate={
                                phase === 'output' 
                                    ? { opacity: 1, scale: 1, y: 0 } 
                                    : { opacity: 0.1, scale: 0.92, y: 5 }
                            }
                            transition={{ 
                                delay: phase === 'output' ? i * 0.15 : 0,
                                duration: 0.7,
                                ease: [0.4, 0, 0.2, 1]
                            }}
                        >
                            <MiniOutfitCard delay={0} index={i} />
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Mobile: Vertical layout - fixed height */}
            <div className="flex sm:hidden flex-col items-center gap-2 min-h-[380px]">
                {/* Input */}
                <ChaoticInput 
                    isTyping={isTyping} 
                    thought={chaoticThoughts[thoughtIndex]}
                />
                
                {/* Flow line 1 */}
                <FlowLineVertical isActive={phase === 'processing' || phase === 'output'} />
                
                {/* Brain */}
                <ProcessingBrain isActive={phase === 'processing'} />
                
                {/* Flow line 2 */}
                <FlowLineVertical isActive={phase === 'output'} />
                
                {/* Output - horizontal scroll on mobile, always reserve space */}
                <div className="flex gap-2 overflow-x-auto pb-2 px-2 -mx-2 scrollbar-hide">
                    {[0, 1, 2].map((i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.9, y: 10 }}
                            animate={
                                phase === 'output' 
                                    ? { opacity: 1, scale: 1, y: 0 } 
                                    : { opacity: 0.1, scale: 0.92, y: 5 }
                            }
                            transition={{ 
                                delay: phase === 'output' ? i * 0.15 : 0,
                                duration: 0.7,
                                ease: [0.4, 0, 0.2, 1]
                            }}
                            className="flex-shrink-0"
                        >
                            <MiniOutfitCard delay={0} index={i} />
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export function WhyMichela() {
    return (
        <section className="py-24 sm:py-32 bg-paper relative overflow-hidden">
            {/* Subtle background gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent-subtle/5 to-transparent pointer-events-none" />
            
            <div className="max-w-6xl mx-auto px-8 sm:px-6 lg:px-8 relative">
                <div className="text-center mb-16 sm:mb-24">
                    <motion.h2 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="font-display text-4xl sm:text-5xl font-semibold text-ink mb-6"
                    >
                        From chaos to <span className="text-ink-secondary italic">confident</span>
                    </motion.h2>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="text-ink-secondary text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed px-6 sm:px-0"
                    >
                        You describe where you're going. Michela handles the rest — considering your clothes, the weather, and what actually works together.
                    </motion.p>
                </div>

                {/* The main animation */}
                <TransformationAnimation />

                {/* Benefit callouts below animation */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="mt-16 sm:mt-20 grid sm:grid-cols-3 gap-8 max-w-3xl mx-auto text-center"
                >
                    <div>
                        <p className="text-2xl sm:text-3xl font-display font-semibold text-ink mb-1">10 sec</p>
                        <p className="text-sm text-ink-tertiary">Average time to get 3 options</p>
                    </div>
                    <div>
                        <p className="text-2xl sm:text-3xl font-display font-semibold text-ink mb-1">Your clothes</p>
                        <p className="text-sm text-ink-tertiary">Built from what you own</p>
                    </div>
                    <div>
                        <p className="text-2xl sm:text-3xl font-display font-semibold text-ink mb-1">Context-aware</p>
                        <p className="text-sm text-ink-tertiary">Weather, occasion, your style</p>
                    </div>
                </motion.div>
            </div>
        </section>
    )
}

export default WhyMichela
